import { Button, Collapse, Flex } from '@chakra-ui/react'
import { type Document } from '@contentful/rich-text-types'
import { type Sys } from 'contentful'
import { useEffect, useState } from 'react'
import { ReactComponent as CloseIcon } from '~/Assets/icons/CloseIcon.svg'
import Container from '~/components/grid/Container/Container'
import { pxToRem } from '~/utils/pxToRem'

import DocumentToReactRender from '../DocumentToReactRender/DocumentToReactRender'

type NotificationType = 'Danger' | 'Warning' | 'Notice'

export interface NotificationBarProps {
  id: string
  type: NotificationType
  content: Document
  entryId: Sys['id']
}

export default function NotificationBar({
  id,
  type,
  content,
  entryId,
}: NotificationBarProps) {
  const messageKey = btoa(id) // Encode message to create a unique key

  const [shouldShow, setShouldShow] = useState(false)

  const hideNotification = () => {
    const shownNotification = JSON.parse(
      localStorage.getItem('shownNotification') || '{}',
    )
    setShouldShow(false)
    shownNotification[messageKey] = true
    localStorage.setItem('shownNotification', JSON.stringify(shownNotification))
  }

  useEffect(() => {
    const shownNotification = JSON.parse(
      localStorage.getItem('shownNotification') || '{}',
    )
    if (shownNotification[messageKey]) {
      return
    }

    setShouldShow(true)
  }, [messageKey])

  const getBgColor = (type: NotificationType) => {
    switch (type) {
      case 'Danger':
        return 'informative.danger.100'
      case 'Warning':
        return 'informative.warning.100'
      case 'Notice':
        return 'informative.success.100'
      default:
        return 'informative.danger.100'
    }
  }

  return (
    <Collapse
      className="notification-bar"
      in={shouldShow}
      role="dialog"
      aria-modal="false"
      aria-label={type}
      aria-describedby={`message-${id}`}
    >
      <Flex
        bgColor={getBgColor(type)}
        py="5px"
        color="white"
        w="100%"
        align="center"
        justify="center"
        position="relative"
        textAlign={{ md: 'center' }}
        minH="40px"
      >
        <Container>
          <DocumentToReactRender
            id={`message-${id}`}
            componentData={content}
            size="sm"
            pr={{ base: '50px', sm: '0' }}
            entryId={entryId}
            fieldId={`siteNotification-${id}`}
          />
        </Container>

        <Button
          variant="icon"
          onClick={hideNotification}
          p="8px"
          fontSize={pxToRem(24)}
          ml="auto"
          position="absolute"
          right="0"
          top="50%"
          transform="translateY(-50%)"
        >
          <CloseIcon
            aria-label="Close notification"
            role="button"
            fill="white"
            width={pxToRem(24)}
            height={pxToRem(24)}
          />
        </Button>
      </Flex>
    </Collapse>
  )
}
